import React from 'react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import {
  Sparkles,
  TrendingUp,
  Star,
  ShieldCheck,
  Zap,
  Search,
  MessageCircle,
  Calendar,
  Briefcase,
  Hammer,
  Wrench,
  Sprout,
  Car,
  Truck,
  MapPin,
  Sofa,
  Heart,
  Code,
  PartyPopper,
  Scissors,
  PawPrint,
  Key,
  Plus,
  Download,
  Users,
} from 'lucide-react';

// ---- Static content ----
const STORE_LINKS = {
  particuliersAppStore: 'https://apps.apple.com/us/app/zotpro/id6760189483',
  particuliersPlayStore: 'https://play.google.com/store/apps/details?id=com.zotpro.vr&pcampaignid=web_share',
  prosAppStore: 'https://apps.apple.com/fr/app/zotpro-services/id6760189658',
  prosPlayStore: 'https://play.google.com/store/apps/details?id=com.zotproprovider.vr',
};

const CATEGORY_ICONS = {
  'Bâtiment & Travaux': Hammer,
  'Nettoyage & Entretien': Sparkles,
  'Jardin & Espaces verts': Sprout,
  'Réparation & Maintenance': Wrench,
  'Automobile (Atelier)': Car,
  'Dépannage Auto Mobile': Truck,
  'Transport & Mobilité': MapPin,
  'Maison & Décoration': Sofa,
  'Services à la personne': Heart,
  'Business': Briefcase,
  'Digital & Création': Code,
  'Événementiel': PartyPopper,
  'Beauté & Bien-être': Scissors,
  'Animaux': PawPrint,
  'Conciergerie & Location': Key,
  'Autre': Plus,
};
const CATEGORY_NAMES = Object.keys(CATEGORY_ICONS);

const PARTICULIERS_BENEFITS = [
  { icon: Search, title: 'Trouvez le pro idéal en quelques secondes', desc: '16 catégories de services à La Réunion, accessibles partout, à tout moment.' },
  { icon: MessageCircle, title: 'Échangez directement par messagerie', desc: 'Comparez les devis et discutez sans intermédiaire avec votre pro.' },
  { icon: ShieldCheck, title: 'Pros locaux et vérifiés', desc: 'Tous les professionnels sont basés à La Réunion et validés par notre équipe.' },
  { icon: Star, title: 'Aucun frais, aucune commission', desc: 'ZotPro est 100% gratuit pour les particuliers. Aucune surprise.' },
];

const PROS_BENEFITS = [
  { icon: TrendingUp, title: 'Recevez des demandes qualifiées', desc: 'Des clients locaux viennent directement à vous, avec des besoins précis.' },
  { icon: Calendar, title: 'Agenda intégré tout-en-un', desc: 'Gérez vos rendez-vous, missions et clients depuis votre smartphone.' },
  { icon: Zap, title: 'Sans prospection, sans engagement', desc: 'Concentrez-vous sur votre métier. Activez et coupez votre abonnement à tout moment.' },
  { icon: Sparkles, title: '3 mois offerts au lancement', desc: 'Profitez de notre offre exceptionnelle, puis 49€/mois sans engagement.' },
];

const STEPS = [
  { num: '01', title: 'Téléchargez ZotPro', desc: 'Disponible gratuitement sur l\'App Store et Google Play.' },
  { num: '02', title: 'Créez votre compte', desc: 'Particulier ou professionnel : choisissez votre profil en 30 secondes.' },
  { num: '03', title: 'Trouvez ou recevez', desc: 'Particuliers : décrivez votre besoin. Pros : recevez les demandes locales.' },
  { num: '04', title: 'Échangez & réservez', desc: 'Mise en relation directe, messagerie intégrée, agenda partagé.' },
];

const TESTIMONIALS = [
  {
    initials: 'ML',
    name: 'Marie L.',
    role: 'Particulière, Saint-Denis',
    quote: 'J\'ai trouvé un plombier en moins d\'une heure un dimanche. Service impeccable, je recommande !',
    rating: 5,
  },
  {
    initials: 'JP',
    name: 'Jérôme P.',
    role: 'Artisan, Saint-Pierre',
    quote: 'En 2 semaines j\'ai eu plus de demandes qu\'avec mes anciennes pubs. Et tout est gratuit pendant 3 mois.',
    rating: 5,
  },
  {
    initials: 'SC',
    name: 'Sandra C.',
    role: 'Particulière, Saint-Paul',
    quote: 'Enfin une appli 100% Réunion, simple et claire. Plus besoin de chercher pendant des heures sur Facebook.',
    rating: 5,
  },
];

const FAQ = [
  {
    q: 'ZotPro est-il vraiment disponible à La Réunion uniquement ?',
    a: 'Oui. ZotPro est conçu spécifiquement pour les habitants et professionnels de l\'île de La Réunion. Tous nos pros sont basés localement.',
  },
  {
    q: 'Combien coûte l\'application pour les particuliers ?',
    a: 'L\'application est 100% gratuite pour les particuliers. Aucune commission, aucun abonnement, aucun frais caché. Vous payez uniquement le service rendu, directement au professionnel.',
  },
  {
    q: 'Quel est le coût pour les professionnels ?',
    a: 'Les pros bénéficient de 3 mois offerts sans engagement à l\'inscription, puis l\'abonnement est de 49€/mois sans engagement. Vous pouvez résilier à tout moment.',
  },
  {
    q: 'Comment télécharger l\'application ?',
    a: 'ZotPro est disponible sur l\'App Store (iOS) et Google Play (Android). Une version Particulier et une version Pro selon votre profil.',
  },
  {
    q: 'Quelles catégories de services sont disponibles ?',
    a: '16 catégories : Bâtiment, Nettoyage, Jardin, Réparation, Auto, Transport, Décoration, Services à la personne, Business, Digital, Événementiel, Beauté, Animaux, Conciergerie et plus.',
  },
  {
    q: 'Comment contacter l\'équipe ZotPro ?',
    a: 'Vous pouvez nous joindre par téléphone au 0693 46 36 28 ou via les coordonnées indiquées en bas de page.',
  },
];

// ---- Reusable store badges with Plausible event tracking ----
const AppleBadge = ({ href, label = 'App Store', testid, audience, section }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`store-badge plausible-event-name=Download plausible-event-platform=iOS plausible-event-audience=${audience} plausible-event-section=${section}`}
    data-testid={testid}
  >
    <svg viewBox="0 0 24 24" className="store-icon" aria-hidden="true">
      <path fill="currentColor" d="M17.05 12.04c-.03-3.13 2.56-4.63 2.68-4.71-1.46-2.13-3.74-2.42-4.55-2.45-1.94-.2-3.78 1.14-4.77 1.14-.98 0-2.5-1.11-4.11-1.08C4.21 4.97 2.32 6.16 1.3 7.99c-2.16 3.74-.55 9.25 1.55 12.27 1.03 1.48 2.25 3.13 3.85 3.07 1.55-.06 2.13-1 4-1 1.86 0 2.4 1 4.03.97 1.67-.03 2.73-1.5 3.74-2.99 1.18-1.72 1.66-3.38 1.68-3.47-.04-.02-3.22-1.23-3.25-4.89zM14.5 3.2C15.34 2.19 15.92.79 15.77-.6c-1.19.05-2.64.78-3.51 1.79-.78.89-1.46 2.32-1.28 3.68 1.33.1 2.69-.67 3.52-1.67z"/>
    </svg>
    <div className="store-text">
      <span className="store-small">Télécharger sur</span>
      <span className="store-big">{label}</span>
    </div>
  </a>
);

const GoogleBadge = ({ href, label = 'Google Play', testid, audience, section }) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={`store-badge plausible-event-name=Download plausible-event-platform=Android plausible-event-audience=${audience} plausible-event-section=${section}`}
    data-testid={testid}
  >
    <svg viewBox="0 0 512 512" className="store-icon" aria-hidden="true">
      <path fill="#FFC107" d="M325.3 234.3L104.6 13l280.8 161.2-60.1 60.1z"/>
      <path fill="#FF3D00" d="M104.6 499L325.3 277.7l60.1 60.1L104.6 499z"/>
      <path fill="#4CAF50" d="M385.4 174.2L104.6 13c-9.7-5.6-21.7-5.5-31.4 0L325.3 234.3l60.1-60.1z"/>
      <path fill="#1976D2" d="M385.4 337.8l-60.1-60.1L73.2 499c9.8 5.7 21.9 5.7 31.6 0l280.6-161.2z"/>
      <path fill="#0277BD" d="M73.2 13c-9.8 5.7-15.4 16.2-15.4 27.7v430.6c0 11.5 5.7 22 15.4 27.7l252-225L73.2 13z" opacity=".3"/>
      <path fill="#FFFFFF" d="M477.9 232L385.4 174.2l-60.1 60.1 60.1 60.1L477.9 280c19.4-12.4 19.4-35.6 0-48z" opacity=".15"/>
    </svg>
    <div className="store-text">
      <span className="store-small">Disponible sur</span>
      <span className="store-big">{label}</span>
    </div>
  </a>
);

const Landing = () => {
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="zp-landing">
      {/* Promo Banner */}
      <div className="zp-banner" data-testid="promo-banner">
        <Sparkles className="zp-sparkle" size={16} />
        <span>Pros — Offre 3 mois offerts sans engagement, puis 49€/mois sans engagement</span>
        <Sparkles className="zp-sparkle" size={16} />
      </div>

      {/* Header */}
      <header className="zp-header" data-testid="main-header">
        <div className="zp-header-inner">
          <a href="/" className="zp-brand" data-testid="logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_pro-service-hub-5/artifacts/w0r3tvio_APP%20ICON.png"
              alt="ZotPro"
              className="zp-logo-img"
            />
          </a>
          <nav className="zp-nav">
            <button className="zp-nav-link" onClick={() => scrollTo('particuliers')} data-testid="nav-particuliers">Particuliers</button>
            <button className="zp-nav-link" onClick={() => scrollTo('pros')} data-testid="nav-pros">Professionnels</button>
            <button className="zp-nav-link" onClick={() => scrollTo('how')} data-testid="nav-how">Comment ça marche</button>
            <button className="zp-nav-link" onClick={() => scrollTo('faq')} data-testid="nav-faq">FAQ</button>
          </nav>
          <Button onClick={() => scrollTo('download')} className="zp-cta-header" data-testid="header-cta">
            <Download size={16} /> Télécharger
          </Button>
        </div>
      </header>

      {/* Hero */}
      <section className="zp-hero" data-testid="hero-section">
        <div className="zp-hero-inner">
          <div className="zp-hero-text">
            <span className="zp-eyebrow" data-testid="hero-eyebrow">
              <span className="zp-dot" /> Désormais disponible
            </span>
            <h1 className="zp-h1" data-testid="hero-title">
              ZotPro est arrivé.<br />
              <span className="zp-accent">Vos services préférés,</span><br />
              à portée de pouce.
            </h1>
            <p className="zp-h1-sub" data-testid="hero-subtitle">
              L&apos;application 100% Île de la Réunion qui connecte particuliers et professionnels
              en quelques secondes. Téléchargez maintenant.
            </p>

            <div className="zp-hero-ctas">
              <div className="zp-cta-group">
                <span className="zp-cta-label"><Users size={14} /> Je suis particulier</span>
                <div className="zp-store-row">
                  <AppleBadge href={STORE_LINKS.particuliersAppStore} audience="Particulier" section="Hero" testid="hero-particulier-apple" />
                  <GoogleBadge href={STORE_LINKS.particuliersPlayStore} audience="Particulier" section="Hero" testid="hero-particulier-google" />
                </div>
              </div>
              <div className="zp-cta-group">
                <span className="zp-cta-label zp-cta-label-pro"><Briefcase size={14} /> Je suis professionnel</span>
                <div className="zp-store-row">
                  <AppleBadge href={STORE_LINKS.prosAppStore} audience="Pro" section="Hero" testid="hero-pro-apple" />
                  <GoogleBadge href={STORE_LINKS.prosPlayStore} audience="Pro" section="Hero" testid="hero-pro-google" />
                </div>
              </div>
            </div>

            <div className="zp-trust">
              <div className="zp-trust-item"><MapPin size={14} /> 100% Réunion</div>
              <div className="zp-trust-item"><ShieldCheck size={14} /> Pros vérifiés</div>
              <div className="zp-trust-item"><Sparkles size={14} /> Sans engagement</div>
            </div>
          </div>

          <div className="zp-hero-visual">
            <div className="zp-hero-glow" aria-hidden="true" />
            <div className="zp-phone-stack">
              <div className="zp-phone-mockup zp-phone-back">
                <div className="zp-phone zp-phone-tilt-right">
                  <div className="zp-phone-btn zp-phone-btn-silent" />
                  <div className="zp-phone-btn zp-phone-btn-vol-up" />
                  <div className="zp-phone-btn zp-phone-btn-vol-down" />
                  <div className="zp-phone-btn zp-phone-btn-power" />
                  <div className="zp-phone-screen">
                    <img src="/images/app-services.jpg" alt="Services ZotPro" />
                  </div>
                  <div className="zp-phone-island" />
                </div>
              </div>
              <div className="zp-phone-mockup zp-phone-front">
                <div className="zp-phone">
                  <div className="zp-phone-btn zp-phone-btn-silent" />
                  <div className="zp-phone-btn zp-phone-btn-vol-up" />
                  <div className="zp-phone-btn zp-phone-btn-vol-down" />
                  <div className="zp-phone-btn zp-phone-btn-power" />
                  <div className="zp-phone-screen">
                    <img src="/images/app-accueil.jpg" alt="Accueil ZotPro" />
                  </div>
                  <div className="zp-phone-island" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Marquee bar */}
      <section className="zp-marquee" data-testid="marquee">
        <div className="zp-marquee-track">
          {[...CATEGORY_NAMES, ...CATEGORY_NAMES].slice(0, 24).map((n, i) => (
            <span key={i} className="zp-marquee-item">{n}</span>
          ))}
        </div>
      </section>

      {/* For Particuliers */}
      <section id="particuliers" className="zp-section" data-testid="particuliers-section">
        <div className="zp-section-inner">
          <div className="zp-split">
            <div className="zp-split-visual zp-visual-left">
              <div className="zp-phone-mockup">
                <div className="zp-phone zp-phone-tilt-left">
                  <div className="zp-phone-btn zp-phone-btn-silent" />
                  <div className="zp-phone-btn zp-phone-btn-vol-up" />
                  <div className="zp-phone-btn zp-phone-btn-vol-down" />
                  <div className="zp-phone-btn zp-phone-btn-power" />
                  <div className="zp-phone-screen">
                    <img src="/images/app-services.jpg" alt="Liste des services ZotPro" />
                  </div>
                  <div className="zp-phone-island" />
                </div>
              </div>
            </div>
            <div className="zp-split-text">
              <span className="zp-tag zp-tag-green" data-testid="particuliers-tag">
                <Users size={14} /> Pour les particuliers
              </span>
              <h2 className="zp-h2">Le bon pro, près de chez vous, en 1 clic.</h2>
              <p className="zp-section-sub">
                Plus besoin de fouiller les réseaux sociaux. ZotPro centralise tous les meilleurs pros de
                l&apos;île dans une application simple, rapide et 100% gratuite pour les particuliers.
              </p>
              <ul className="zp-benefits">
                {PARTICULIERS_BENEFITS.map((b, i) => (
                  <li key={i} className="zp-benefit">
                    <span className="zp-benefit-icon"><b.icon size={20} /></span>
                    <div>
                      <h4>{b.title}</h4>
                      <p>{b.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="zp-store-row" data-testid="particuliers-stores">
                <AppleBadge href={STORE_LINKS.particuliersAppStore} audience="Particulier" section="ParticuliersSection" testid="part-store-apple" />
                <GoogleBadge href={STORE_LINKS.particuliersPlayStore} audience="Particulier" section="ParticuliersSection" testid="part-store-google" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* For Pros */}
      <section id="pros" className="zp-section zp-section-alt" data-testid="pros-section">
        <div className="zp-section-inner">
          <div className="zp-split zp-split-reverse">
            <div className="zp-split-text">
              <span className="zp-tag zp-tag-amber" data-testid="pros-tag">
                <Briefcase size={14} /> Pour les professionnels
              </span>
              <h2 className="zp-h2">Recevez des clients sans prospecter.</h2>
              <p className="zp-section-sub">
                Concentrez-vous sur votre métier. ZotPro vous apporte des demandes locales qualifiées,
                un agenda intégré et zéro engagement. 3 mois offerts pour démarrer.
              </p>
              <ul className="zp-benefits">
                {PROS_BENEFITS.map((b, i) => (
                  <li key={i} className="zp-benefit">
                    <span className="zp-benefit-icon zp-benefit-icon-pro"><b.icon size={20} /></span>
                    <div>
                      <h4>{b.title}</h4>
                      <p>{b.desc}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="zp-store-row" data-testid="pros-stores">
                <AppleBadge href={STORE_LINKS.prosAppStore} audience="Pro" section="ProsSection" testid="pro-store-apple" />
                <GoogleBadge href={STORE_LINKS.prosPlayStore} audience="Pro" section="ProsSection" testid="pro-store-google" />
              </div>
            </div>
            <div className="zp-split-visual">
              <div className="zp-phone-mockup">
                <div className="zp-phone zp-phone-tilt-right">
                  <div className="zp-phone-btn zp-phone-btn-silent" />
                  <div className="zp-phone-btn zp-phone-btn-vol-up" />
                  <div className="zp-phone-btn zp-phone-btn-vol-down" />
                  <div className="zp-phone-btn zp-phone-btn-power" />
                  <div className="zp-phone-screen">
                    <img src="/images/app-reservations.jpg" alt="Écran réservations ZotPro" />
                  </div>
                  <div className="zp-phone-island" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="zp-section" data-testid="how-section">
        <div className="zp-section-inner">
          <div className="zp-section-header">
            <span className="zp-tag" data-testid="how-tag"><Sparkles size={14} /> Comment ça marche</span>
            <h2 className="zp-h2 zp-center">4 étapes, c&apos;est tout.</h2>
            <p className="zp-section-sub zp-center">De l&apos;installation à la première mission en quelques minutes.</p>
          </div>
          <div className="zp-steps">
            {STEPS.map((s, i) => (
              <div key={i} className="zp-step" data-testid={`step-${i + 1}`}>
                <div className="zp-step-num">{s.num}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="zp-section zp-section-alt" data-testid="categories-section">
        <div className="zp-section-inner">
          <div className="zp-section-header">
            <span className="zp-tag"><Hammer size={14} /> Tous les métiers</span>
            <h2 className="zp-h2 zp-center">16 catégories. Plus de 150 spécialités.</h2>
            <p className="zp-section-sub zp-center">
              Du bâtiment à la beauté, en passant par le digital et l&apos;événementiel — ZotPro couvre tous vos besoins.
            </p>
          </div>
          <div className="zp-cat-grid">
            {CATEGORY_NAMES.map((name) => {
              const Icon = CATEGORY_ICONS[name];
              return (
                <div key={name} className="zp-cat-card" data-testid={`cat-${name}`}>
                  <Icon size={26} className="zp-cat-icon" />
                  <span>{name}</span>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="zp-section" data-testid="testimonials-section">
        <div className="zp-section-inner">
          <div className="zp-section-header">
            <span className="zp-tag"><Star size={14} /> Ils en parlent</span>
            <h2 className="zp-h2 zp-center">Ce qu&apos;en disent les Réunionnais.</h2>
          </div>
          <div className="zp-testimonials">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="zp-test-card" data-testid={`testimonial-${i + 1}`}>
                <div className="zp-stars">
                  {[...Array(t.rating)].map((_, idx) => (
                    <Star key={idx} size={16} fill="#22C55E" stroke="#22C55E" />
                  ))}
                </div>
                <p className="zp-quote">&ldquo;{t.quote}&rdquo;</p>
                <div className="zp-author">
                  <div className="zp-avatar">{t.initials}</div>
                  <div>
                    <h5>{t.name}</h5>
                    <span>{t.role}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="zp-section zp-section-alt" data-testid="faq-section">
        <div className="zp-section-inner zp-section-narrow">
          <div className="zp-section-header">
            <span className="zp-tag"><MessageCircle size={14} /> Questions fréquentes</span>
            <h2 className="zp-h2 zp-center">Tout ce que vous devez savoir.</h2>
          </div>
          <Accordion type="single" collapsible className="zp-faq">
            {FAQ.map((item, i) => (
              <AccordionItem key={i} value={`item-${i}`} className="zp-faq-item" data-testid={`faq-${i + 1}`}>
                <AccordionTrigger className="zp-faq-trigger">{item.q}</AccordionTrigger>
                <AccordionContent className="zp-faq-content">{item.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Final Download CTA */}
      <section id="download" className="zp-download" data-testid="download-section">
        <div className="zp-download-inner">
          <h2 className="zp-h2 zp-center zp-download-title">
            Téléchargez ZotPro<br /><span className="zp-accent">et rejoignez la communauté.</span>
          </h2>
          <p className="zp-section-sub zp-center">
            L&apos;application 100% Réunion, disponible dès maintenant sur iOS et Android.
          </p>
          <div className="zp-download-grid">
            <div className="zp-download-card">
              <div className="zp-download-card-head">
                <Users size={22} /> <span>Particuliers</span>
              </div>
              <p>Gratuit · Sans abonnement · Sans frais cachés</p>
              <div className="zp-store-row">
                <AppleBadge href={STORE_LINKS.particuliersAppStore} audience="Particulier" section="FinalCTA" testid="final-part-apple" />
                <GoogleBadge href={STORE_LINKS.particuliersPlayStore} audience="Particulier" section="FinalCTA" testid="final-part-google" />
              </div>
            </div>
            <div className="zp-download-card zp-download-card-pro">
              <div className="zp-download-card-head">
                <Briefcase size={22} /> <span>Professionnels</span>
              </div>
              <p>3 mois offerts · puis 49€/mois · Sans engagement</p>
              <div className="zp-store-row">
                <AppleBadge href={STORE_LINKS.prosAppStore} audience="Pro" section="FinalCTA" testid="final-pro-apple" />
                <GoogleBadge href={STORE_LINKS.prosPlayStore} audience="Pro" section="FinalCTA" testid="final-pro-google" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="zp-footer" data-testid="footer">
        <div className="zp-footer-inner">
          <div className="zp-footer-brand">
            <img
              src="https://customer-assets.emergentagent.com/job_pro-service-hub-5/artifacts/w0r3tvio_APP%20ICON.png"
              alt="ZotPro"
              className="zp-logo-img zp-logo-footer"
            />
            <p>L&apos;application 100% Réunion qui connecte particuliers et pros locaux.</p>
          </div>
          <div className="zp-footer-cols">
            <div className="zp-footer-col">
              <h5>Application</h5>
              <button className="zp-footer-link" onClick={() => scrollTo('particuliers')}>Particuliers</button>
              <button className="zp-footer-link" onClick={() => scrollTo('pros')}>Pros</button>
              <button className="zp-footer-link" onClick={() => scrollTo('how')}>Comment ça marche</button>
              <button className="zp-footer-link" onClick={() => scrollTo('faq')}>FAQ</button>
            </div>
            <div className="zp-footer-col">
              <h5>Légal</h5>
              <a href="/mentions-legales" className="zp-footer-link">Mentions légales</a>
              <a href="/politique-confidentialite" className="zp-footer-link">Politique de confidentialité</a>
            </div>
            <div className="zp-footer-col">
              <h5>Contact</h5>
              <a href="tel:0693463628" className="zp-footer-link">0693 46 36 28</a>
              <span className="zp-footer-meta">Île de La Réunion</span>
            </div>
          </div>
        </div>
        <div className="zp-footer-base">
          © 2025 ZotPro — SARL DEALRUN. Tous droits réservés.
        </div>
      </footer>
    </div>
  );
};

export default Landing;
